import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Target, Trash2, Users, Coins, Radio, Activity, TimerReset, Vote, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Tools section — Stream Goals + Live Polls.
 *
 * Two panels stacked vertically. Both use the same auth (session JWT via api.js)
 * and hit the /streamer/goals + /streamer/polls endpoints.
 */
export default function Tools() {
  return (
    <div className="space-y-6" data-testid="section-tools">
      <GoalsPanel />
      <PollsPanel />
    </div>
  );
}

/* ----------------------- Goals ----------------------- */
const GOAL_META = {
  donation:    { label: "Donation",   icon: Coins,  color: "orange", hint: "USD raised via tips" },
  subscribers: { label: "Subscribers", icon: Users, color: "cyan",   hint: "New channel subs" },
  followers:   { label: "Followers",   icon: Radio, color: "pink",   hint: "New followers" },
};

function GoalsPanel() {
  const [goals, setGoals] = useState([]);
  const [type, setType] = useState("donation");
  const [label, setLabel] = useState("");
  const [target, setTarget] = useState("100");

  const load = useCallback(() => api.get("/streamer/goals").then((r) => setGoals(r.data.goals || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!label.trim() || !target) return toast.error("Fill in label + target");
    try {
      await api.post("/streamer/goals", { type, label: label.trim(), target: Number(target) });
      setLabel(""); setTarget("100");
      load();
      toast.success("Goal created");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/streamer/goals/${id}`); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <div className="rounded-xl p-5" style={{ background: "#0e0e13", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="goals-panel">
      <div className="flex items-center gap-2 mb-3"><Target className="w-5 h-5 text-cyan-400" /><h3 className="text-lg font-semibold">Stream Goals</h3></div>
      <p className="text-xs text-zinc-500 mb-4">One active goal per type. Progress auto-updates when tips, subs, and followers land.</p>

      <div className="grid grid-cols-1 md:grid-cols-[140px,1fr,120px,auto] gap-2 mb-4">
        <select value={type} onChange={(e) => setType(e.target.value)} className="bg-zinc-900 border border-white/10 rounded px-2 py-2 text-sm" data-testid="goal-type-select">
          {Object.entries(GOAL_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Goal label (e.g. New PC by Friday)" className="bg-zinc-900 border border-white/10 rounded px-3 py-2 text-sm" data-testid="goal-label-input" />
        <input value={target} onChange={(e) => setTarget(e.target.value)} type="number" min="1" placeholder="Target" className="bg-zinc-900 border border-white/10 rounded px-3 py-2 text-sm" data-testid="goal-target-input" />
        <button onClick={create} className="bg-pink-600 hover:bg-pink-500 rounded px-4 py-2 text-sm font-semibold flex items-center gap-2" data-testid="goal-add-btn"><Plus className="w-4 h-4" />Add</button>
      </div>

      <div className="space-y-2" data-testid="goals-list">
        {goals.length === 0 && <div className="text-sm text-zinc-500 italic">No goals yet. Add your first above.</div>}
        {goals.map((g) => {
          const M = GOAL_META[g.type] || GOAL_META.donation;
          const pct = Math.min(100, Math.round((g.current / Math.max(g.target, 1)) * 100));
          return (
            <div key={g.id} className="rounded-lg border border-white/10 p-3 flex items-center gap-3" data-testid={`goal-row-${g.id}`}>
              <M.icon className={`w-5 h-5 text-${M.color}-400`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-sm"><span className="font-semibold truncate">{g.label}</span><span className="text-zinc-500 text-xs">{M.label}</span></div>
                <div className="mt-1 h-2 bg-zinc-800 rounded overflow-hidden"><div className={`h-full bg-${M.color}-500`} style={{ width: `${pct}%` }} /></div>
                <div className="mt-1 text-xs text-zinc-400 sc-mono">{g.current.toFixed(1)} / {g.target} · {pct}%</div>
              </div>
              <button onClick={() => remove(g.id)} className="p-2 rounded hover:bg-white/5" data-testid={`goal-delete-${g.id}`} title="Remove"><Trash2 className="w-4 h-4 text-zinc-500" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ----------------------- Polls ----------------------- */
function PollsPanel() {
  const [poll, setPoll] = useState(null);
  const [q, setQ] = useState("");
  const [choices, setChoices] = useState(["", ""]);
  const [duration, setDuration] = useState(60);
  const [allowMulti, setAllowMulti] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadActive = useCallback(async () => {
    // No dedicated "my active poll" endpoint; reuse public endpoint via username.
    try {
      const me = await api.get("/auth/me");
      const r = await api.get(`/channels/${me.data.username}/polls/active`);
      setPoll(r.data.poll);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadActive(); const t = setInterval(loadActive, 3000); return () => clearInterval(t); }, [loadActive]);

  const setChoice = (i, v) => setChoices((c) => c.map((x, k) => (k === i ? v : x)));
  const addChoice = () => setChoices((c) => (c.length < 6 ? [...c, ""] : c));
  const rmChoice = (i) => setChoices((c) => (c.length > 2 ? c.filter((_, k) => k !== i) : c));

  const create = async () => {
    const clean = choices.map((s) => s.trim()).filter(Boolean);
    if (!q.trim() || clean.length < 2) return toast.error("Question + 2+ choices required");
    setLoading(true);
    try {
      const r = await api.post("/streamer/polls", { question: q.trim(), choices: clean, duration_seconds: Number(duration), allow_multi: allowMulti });
      setPoll(r.data);
      setQ(""); setChoices(["", ""]);
      toast.success("Poll started");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
    finally { setLoading(false); }
  };

  const close = async () => {
    if (!poll) return;
    try { const r = await api.post(`/streamer/polls/${poll.id}/close`); setPoll(r.data); toast("Poll closed"); }
    catch { toast.error("Could not close"); }
  };

  return (
    <div className="rounded-xl p-5" style={{ background: "#0e0e13", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="polls-panel">
      <div className="flex items-center gap-2 mb-3"><Vote className="w-5 h-5 text-pink-400" /><h3 className="text-lg font-semibold">Live Polls</h3></div>

      {poll && !poll.closed ? (
        <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-4" data-testid="poll-active">
          <div className="text-sm font-semibold mb-1">{poll.question}</div>
          <div className="text-[11px] sc-mono text-zinc-500 mb-3">{poll.total_votes} votes · {poll.allow_multi ? "multi-vote" : "single-vote"} · ends {new Date(poll.ends_at).toLocaleTimeString()}</div>
          <div className="space-y-2">
            {poll.choices.map((c, i) => {
              const tally = poll.tallies[i] || 0;
              const pct = poll.total_votes ? Math.round((tally / poll.total_votes) * 100) : 0;
              return (
                <div key={i} className="text-sm">
                  <div className="flex justify-between"><span>{c}</span><span className="sc-mono text-xs text-zinc-400">{tally} · {pct}%</span></div>
                  <div className="mt-1 h-2 bg-zinc-800 rounded overflow-hidden"><div className="h-full bg-cyan-400" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
          <button onClick={close} className="mt-4 bg-zinc-800 hover:bg-zinc-700 rounded px-3 py-1.5 text-xs sc-mono flex items-center gap-2" data-testid="poll-close-btn"><TimerReset className="w-3.5 h-3.5" />Close poll</button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="poll-create-form">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Poll question…" className="w-full bg-zinc-900 border border-white/10 rounded px-3 py-2 text-sm" data-testid="poll-question-input" />
          <div className="space-y-2">
            {choices.map((c, i) => (
              <div key={i} className="flex gap-2">
                <input value={c} onChange={(e) => setChoice(i, e.target.value)} placeholder={`Choice ${i + 1}`} className="flex-1 bg-zinc-900 border border-white/10 rounded px-3 py-2 text-sm" data-testid={`poll-choice-${i}`} />
                {choices.length > 2 && <button onClick={() => rmChoice(i)} className="p-2 rounded hover:bg-white/5" data-testid={`poll-choice-remove-${i}`}><Trash2 className="w-4 h-4 text-zinc-500" /></button>}
              </div>
            ))}
            {choices.length < 6 && <button onClick={addChoice} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1" data-testid="poll-choice-add"><Plus className="w-3.5 h-3.5" />add choice</button>}
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs sc-mono text-zinc-400 flex items-center gap-2">Duration<input type="number" min="15" max="600" value={duration} onChange={(e) => setDuration(e.target.value)} className="w-20 bg-zinc-900 border border-white/10 rounded px-2 py-1 text-sm" data-testid="poll-duration-input" />s</label>
            <label className="text-xs sc-mono text-zinc-400 flex items-center gap-2"><input type="checkbox" checked={allowMulti} onChange={(e) => setAllowMulti(e.target.checked)} data-testid="poll-multi-checkbox" />allow multi-vote</label>
          </div>
          <button onClick={create} disabled={loading} className="bg-pink-600 hover:bg-pink-500 disabled:opacity-50 rounded px-4 py-2 text-sm font-semibold flex items-center gap-2" data-testid="poll-start-btn"><Activity className="w-4 h-4" />Start poll</button>
        </div>
      )}
    </div>
  );
}
